import { Flex } from '@chakra-ui/react';
import React from 'react';

import { isMac } from 'helpers/environment';
import { isNotNull } from 'helpers/typescript';

const CTRL = isMac ? '⌃' : 'Ctrl';
export const MODIFIER_KEY = isMac ? '⌘' : CTRL;
const OPTION_KEY = isMac ? '⌥' : 'Alt';
const SHIFT = '⇧';

type KeyboardShortcutModifiers = {
  meta?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  option?: boolean;
};

const getKeyboardShortcutHotkeyString = (
  key: string,
  { meta = false, ctrl = false, shift = false, option = false }: KeyboardShortcutModifiers = {},
): string => {
  let prefixes = [''];

  if (shift) {
    prefixes = prefixes.map((p) => `Shift+${p}`);
  }
  if (option) {
    prefixes = prefixes.flatMap((p) => [`Option+${p}`, `Alt+${p}`]);
  }
  if (meta) {
    prefixes = prefixes.flatMap((p) => [`Cmd+${p}`, `Ctrl+${p}`]);
  } else if (ctrl) {
    prefixes = prefixes.map((p) => `Ctrl+${p}`);
  }

  const keyStrings = SPECIAL_KEYS_MAP[key] ?? [key];
  const shortcuts = prefixes.flatMap((prefix) =>
    keyStrings.map((keyString) => `${prefix}${keyString}`),
  );

  return shortcuts.join(', ');
};

type ShortcutEntry = {
  label: React.ReactNode;
  hotkeyString: string;
};

const getKeyboardShortcut = (
  key: string,
  { meta = false, ctrl = false, shift = false, option = false }: KeyboardShortcutModifiers = {},
): ShortcutEntry => {
  return {
    label: <Shortcut selection={key} meta={meta} ctrl={ctrl} shift={shift} option={option} />,
    hotkeyString: getKeyboardShortcutHotkeyString(key, { meta, ctrl, shift, option }),
  };
};

const Shortcut = ({
  selection,
  meta = false,
  ctrl = false,
  shift = false,
  option = false,
}: {
  selection: string;
  meta?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  option?: boolean;
}) => {
  const keys = [
    meta ? MODIFIER_KEY : null,
    ctrl ? CTRL : null,
    option ? OPTION_KEY : null,
    shift ? SHIFT : null,
    selection,
  ].filter(isNotNull);

  return (
    <Flex alignItems="center" gap="2px" fontSize="xs">
      {keys.map((key, index) => {
        return (
          <React.Fragment key={key}>
            <Flex>{key}</Flex>
            {!isMac && index < keys.length - 1 && <Flex>+</Flex>}
          </React.Fragment>
        );
      })}
    </Flex>
  );
};

const SPECIAL_KEYS_MAP: Record<string, string[]> = {
  '⏎': ['Enter'],
  '⌫': ['Backspace', 'Delete'],
  '↑': ['Up'],
  '↓': ['Down'],
  '←': ['Left'],
  '→': ['Right'],
};

// Unfortunately, we can't use the react-hotkeys-hook to handle indent/outdent hotkeys so
// storing the key codes here to use in our custom event listener.
export const INDENT_KEY_CODE = 'BracketRight';
export const OUTDENT_KEY_CODE = 'BracketLeft';
export const eventHasIndentOutdentModifiers = (event: KeyboardEvent) => {
  return event.metaKey && event.altKey;
};

export const KEYBOARD_SHORTCUTS: Record<string, ShortcutEntry> = {
  delete: getKeyboardShortcut('⌫'),
  shiftDelete: getKeyboardShortcut('⌫', { shift: true }),
  indent: getKeyboardShortcut(']', { meta: true, option: true }),
  outdent: getKeyboardShortcut('[', { meta: true, option: true }),
  bold: getKeyboardShortcut('B', { meta: true }),
  italic: getKeyboardShortcut('I', { meta: true }),
  strike: getKeyboardShortcut('X', { meta: true, shift: true }),
  enter: getKeyboardShortcut('⏎'),
  shiftEnter: getKeyboardShortcut('⏎', { shift: true }),
  metaEnter: getKeyboardShortcut('⏎', { meta: true }),
  metaOptionEnter: getKeyboardShortcut('⏎', { meta: true, option: true }),
  escape: getKeyboardShortcut('Esc'),
  search: getKeyboardShortcut('K', { meta: true }),
  undo: getKeyboardShortcut('Z', { meta: true }),
  redo: getKeyboardShortcut('Z', { meta: true, shift: true }),
  tab: getKeyboardShortcut('Tab'),
  shiftTab: getKeyboardShortcut('Tab', { shift: true }),
  selectAll: getKeyboardShortcut('A', { meta: true }),
  up: getKeyboardShortcut('↑'),
  down: getKeyboardShortcut('↓'),
  metaUp: getKeyboardShortcut('↑', { meta: true }),
  metaDown: getKeyboardShortcut('↓', { meta: true }),
  shiftUp: getKeyboardShortcut('↑', { shift: true }),
  shiftDown: getKeyboardShortcut('↓', { shift: true }),
  metaShiftUp: getKeyboardShortcut('↑', { meta: true, shift: true }),
  metaShiftDown: getKeyboardShortcut('↓', { meta: true, shift: true }),
  left: getKeyboardShortcut('←'),
  right: getKeyboardShortcut('→'),
  metaLeft: getKeyboardShortcut('←', { meta: true }),
  metaRight: getKeyboardShortcut('→', { meta: true }),
  shiftLeft: getKeyboardShortcut('←', { shift: true }),
  shiftRight: getKeyboardShortcut('→', { shift: true }),
  metaShiftLeft: getKeyboardShortcut('←', { meta: true, shift: true }),
  metaShiftRight: getKeyboardShortcut('→', { meta: true, shift: true }),
};

export type KeyboardShortcut = keyof typeof KEYBOARD_SHORTCUTS;
